import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router';

import { PinChangeDecisionForm, PinChangeOption } from '@eika-kortbetjening/pin-change-decision-form';

import SkeletonList from '@/components/skeleton/SkeletonList';
import piwik from '@/piwik/piwik';
import { useCard } from '@/react-query/hooks/query/use-card';
import { navigateToCardDetails } from '@/utils/url/get-card-details-url-utils';
import { getRootUrl, navigateToHardBlockConfirm, navigateToNewPinEntry } from '@/utils/url/get-url-utils';

import { useLinkProducer, useNavigate } from '@domain/app';
import useReasonQuery from '@hooks/query/use-reason-query';
import { useIsHaugesund } from '@hooks/use-bank-type';
import useText from '@hooks/use-text';

const PinChangeDecision = () => {
    const [selectedOption, setSelectedOption] = useState<PinChangeOption | undefined>(PinChangeOption.KEEP_PIN);
    const [pinChangeDecisionError, setPinChangeDecisionError] = useState<string | undefined>(undefined);

    const { data: card, isLoading } = useCard();
    const text = useText();
    const reasonQuery = useReasonQuery();
    const blockPinChange = useIsHaugesund();
    const navigate = useNavigate();
    const location = useLocation();
    const linkProducer = useLinkProducer();

    const { formPleaseFillOutAlternative } = text;

    useEffect(() => {
        if (typeof selectedOption !== 'undefined') {
            setPinChangeDecisionError(undefined);
        }
    }, [selectedOption]);

    const next = useCallback(() => {
        if (typeof selectedOption === 'undefined') {
            setPinChangeDecisionError(formPleaseFillOutAlternative);
            return;
        }

        if (card && reasonQuery) {
            if (selectedOption === PinChangeOption.KEEP_PIN) {
                navigateToHardBlockConfirm(navigate)({ card, selectedReason: reasonQuery, reorder: true });
                return;
            }

            if (selectedOption === PinChangeOption.CHANGE_PIN) {
                navigateToNewPinEntry(navigate, location);
            }
        }
    }, [card, formPleaseFillOutAlternative, location, navigate, reasonQuery, selectedOption]);

    useEffect(() => {
        if (blockPinChange) {
            setSelectedOption(PinChangeOption.KEEP_PIN);
            next();
        }
    }, [blockPinChange, next]);

    if (typeof card === 'undefined' || isLoading) {
        return <SkeletonList />;
    }

    if (reasonQuery === null) {
        return <Navigate to={getRootUrl(linkProducer)} replace />;
    }

    return (
        <PinChangeDecisionForm
            cancel={() => navigateToCardDetails(navigate)(card)}
            next={next}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            pinChangeDecisionError={pinChangeDecisionError}
            sitecoreDictionary={text}
            piwik={piwik}
        />
    );
};

export default PinChangeDecision;
